import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Panel, Button, IconButton, Field, TextInput, Tag, Banner, Spinner, Select, Checkbox, RadioList, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { apiError, fmtDate, Row, Toggle } from '../../next/ui';

const FRAMES = [
  { value: 'daf_yomi', label: 'דף היומי' },
  { value: 'shinun', label: 'דף השינון' },
  { value: 'meshannim', label: 'משננים' },
];
const FRAME_LABEL = Object.fromEntries(FRAMES.map((f) => [f.value, f.label]));
const DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 10, 14].map((d) => ({
  value: String(d),
  label: d === 1 ? 'רק ביום הפתיחה' : `${d} ימים מיום הפתיחה`,
}));
const POSITION_OPTIONS = [
  { value: 'after_welcome', label: 'אחרי «ברוכים הבאים»' },
  { value: 'before_welcome', label: 'לפני «ברוכים הבאים»' },
];
const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|wma|webm)$/i;

function StatusBanner({ status }) {
  if (!status) return null;
  if (!status.enabled) return <Banner tone="warning" icon="info">ההודעה כבויה — לא מושמעת בפתיח.</Banner>;
  if (!status.playing) return <Banner tone="info" icon="info">לא מושמע היום — אין מבחן שנפתח בטווח שהוגדר.</Banner>;
  return (
    <Banner tone="success" icon="check">
      <b>מושמע היום בפתיח.</b>
      {status.manual ? ' (חלון ידני)' : ''}
      {status.exams.map((e) => (
        <div key={e._id}>
          {`«${e.title}» · ${FRAME_LABEL[e.frame] || e.frame} · נפתח ${fmtDate(e.openedAt, true)} · עד ${fmtDate(e.until, true)}`}
        </div>
      ))}
    </Banner>
  );
}

/**
 * "המבחנים נפתחו במערכת" (קטלוג 243) בפתיח /77 — /mishtameret-intro/exams-opened:
 * מתי מושמע (ימים מפתיחת מבחן, מסגרות, חלון ידני), לפני/אחרי 001, וההקלטה עצמה מהקטלוג.
 */
export default function IntroExamsOpenedPanel() {
  const toast = useCkToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: res } = await api.get('/mishtameret-intro/exams-opened');
      setData(res);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינה'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { audioRef.current?.pause(); audioRef.current = null; }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data: res } = await api.put('/mishtameret-intro/exams-opened', patch);
      setData(res);
      toast.success('נשמר');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בשמירה'));
    } finally {
      setSaving(false);
    }
  };

  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
  };

  const play = async () => {
    if (playing) { stopAudio(); return; }
    try {
      const { data: blob } = await api.get(`/narration-prompts/${data.prompt._id}/audio`, { responseType: 'blob' });
      if (blob.type?.includes('application/json')) {
        let text = 'אין קובץ להשמעה';
        try { text = JSON.parse(await blob.text()).error || text; } catch { /* ignore */ }
        toast.error(text);
        return;
      }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); stopAudio(); };
      audio.onerror = () => { URL.revokeObjectURL(url); toast.error('לא ניתן להשמיע את הקובץ'); stopAudio(); };
      audioRef.current = audio;
      setPlaying(true);
      audio.play().catch(() => setPlaying(false));
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהשמעה'));
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('audio/') && !AUDIO_EXT_RE.test(file.name || '')) {
      toast.error('יש להעלות קובץ שמע');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      await api.post(`/narration-prompts/${data.prompt._id}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('ההקלטה הועלתה');
      await load();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהעלאה'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const s = data?.settings;
  const toggleFrame = (frame, on) => {
    const current = s.frames?.length ? s.frames : FRAMES.map((f) => f.value);
    const next = on ? [...new Set([...current, frame])] : current.filter((f) => f !== frame);
    if (!next.length) { toast.error('יש לבחור לפחות מסגרת אחת'); return; }
    save({ frames: next.length === FRAMES.length ? [] : next });
  };

  return (
    <Panel
      title="«המבחנים נפתחו במערכת» (243)"
      sub="מושמע בפתיח כשנפתח מבחן"
      icon="bell"
      tone="info"
      actions={s ? (
        <span className={`nx-toggle ${s.enabled ? 'is-on' : 'is-off'}`}>
          {saving ? <Spinner size={16} /> : null}
          {s.enabled ? 'פעיל' : 'כבוי'}
          <Toggle checked={s.enabled} disabled={saving} onChange={() => save({ enabled: !s.enabled })} label={s.enabled ? 'כיבוי' : 'הפעלה'} />
        </span>
      ) : null}
    >
      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner> : null}
      {!data && !error ? <div className="ck-empty"><Spinner size={22} /></div> : null}
      {s ? (
        <div className="nx-stack">
          <StatusBanner status={data.status} />

          {data.prompt ? (
            <div className="nx-item">
              <IconButton icon={playing ? 'pause' : 'play'} label={playing ? 'עצירה' : 'השמעה'} disabled={!data.prompt.hasAudio} onClick={play} />
              <div className="nx-item__main">
                <b>{data.prompt.text}</b>
                <small>{data.prompt.hasAudio ? 'הקלטה מהקטלוג (243)' : 'עדיין אין הקלטה — מושמע בקול ממוחשב (TTS)'}</small>
              </div>
              <div className="nx-item__acts">
                <Tag tone={data.prompt.hasAudio ? 'success' : 'warning'}>{data.prompt.hasAudio ? 'הוקלט' : 'TTS'}</Tag>
                <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" hidden onChange={(e) => upload(e.target.files?.[0])} />
                <Button size="sm" variant="ghost" icon="upload" disabled={uploading} onClick={() => fileRef.current?.click()}>
                  {uploading ? 'מעלה…' : data.prompt.hasAudio ? 'החלפת הקלטה' : 'העלאת הקלטה'}
                </Button>
              </div>
            </div>
          ) : <Banner tone="warning" icon="alert">קוד 243 חסר בקטלוג הקריינות.</Banner>}

          <Row align="flex-end">
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Field label="כמה זמן מושמע">
                <Select value={String(s.days)} disabled={saving} onChange={(v) => save({ days: Number(v) })} options={DAYS_OPTIONS} />
              </Field>
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <Field label="מיקום בפתיח">
                <RadioList inline value={s.position} disabled={saving} onChange={(position) => save({ position })} options={POSITION_OPTIONS} ariaLabel="מיקום בפתיח" />
              </Field>
            </div>
          </Row>
          <p className="nx-desc ck-muted">
            ההודעה מתחילה ברגע שהמבחן נפתח (לפי תאריך הפתיחה, או מיד כשנפתח בלי תאריך) ומסתיימת בחצות של היום האחרון בטווח.
          </p>

          <Field label="מבחנים שמפעילים את ההודעה">
            <Row>
              {FRAMES.map((f) => (
                <Checkbox
                  key={f.value}
                  checked={!s.frames?.length || s.frames.includes(f.value)}
                  disabled={saving}
                  onChange={(on) => toggleFrame(f.value, on)}
                  label={f.label}
                />
              ))}
            </Row>
          </Field>

          <Field label="השמעה ידנית בטווח תאריכים (גם בלי מבחן שנפתח)">
            <Row align="flex-end">
              <TextInput type="date" aria-label="מתאריך" value={s.manualFrom || ''} max={s.manualUntil || undefined} disabled={saving} onChange={(e) => save({ manualFrom: e.target.value })} />
              <span>עד</span>
              <TextInput type="date" aria-label="עד תאריך" value={s.manualUntil || ''} min={s.manualFrom || undefined} disabled={saving} onChange={(e) => save({ manualUntil: e.target.value })} />
              {s.manualFrom || s.manualUntil
                ? <Button size="sm" variant="ghost" icon="x" disabled={saving} onClick={() => save({ manualFrom: '', manualUntil: '' })}>ניקוי</Button>
                : null}
            </Row>
          </Field>
        </div>
      ) : null}
    </Panel>
  );
}
